import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../data/translations';
import { API_ENDPOINTS } from '../config/apiConfig';
import './Auth.css';

const Register = () => {
  const [searchParams] = useSearchParams();
  const isPremiumSignup = searchParams.get('premium') === 'true';
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { language } = useLanguage();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.username || !formData.email || !formData.password || !formData.confirmPassword) {
      setError(getTranslation(language, 'allFieldsRequired'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(getTranslation(language, 'passwordsDoNotMatch'));
      return;
    }

    if (formData.password.length < 6) {
      setError(getTranslation(language, 'passwordMinLength'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.REGISTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          subscription: isPremiumSignup ? 'premium' : 'free'
        })
      });

      // Handle network errors
      if (!response.ok && response.status === 0) {
        throw new Error('Network error. Please check your connection and try again.');
      }

      // Try to parse JSON
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        console.error('Response status:', response.status);
        console.error('Response text:', await response.text());
        throw new Error('Server error: Invalid response. Please try again later.');
      }

      if (!response.ok) {
        throw new Error(data.error || `Registration failed (${response.status})`);
      }

      // Validate response has required fields
      if (!data.token || !data.user) {
        throw new Error('Invalid server response: missing token or user data');
      }

      // Save token and user data
      login(data.token, data.user);

      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h2>{getTranslation(language, 'register')}</h2>
        <p className="auth-subtitle">
          {isPremiumSignup ? '🎉 ' + getTranslation(language, 'register') : getTranslation(language, 'register')}
        </p>
        
        {isPremiumSignup && (
          <div className="premium-badge-register">
            <span className="premium-icon">⭐</span>
            <span>Premium Account</span>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">{getTranslation(language, 'username')}</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder={getTranslation(language, 'username')}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">{getTranslation(language, 'email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={getTranslation(language, 'email')}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{getTranslation(language, 'password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={getTranslation(language, 'password')}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">{getTranslation(language, 'confirmPassword')}</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={getTranslation(language, 'confirmPassword')}
              disabled={loading}
            />
          </div>

          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? getTranslation(language, 'loading') : getTranslation(language, 'register')}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            {getTranslation(language, 'alreadyHaveAccount')} <Link to="/login">{getTranslation(language, 'login')}</Link>
          </p>
          <p>
            <Link to="/" className="auth-home-link">← {getTranslation(language, 'home')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
